import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useRef, type RefObject } from 'react'
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  DynamicDrawUsage,
  Material,
  Points,
  Quaternion,
  ShaderMaterial,
  TextureLoader,
  Vector3,
  type Object3D,
  type Vector3Tuple,
} from 'three'

import { getVehicleById, type Locomotive } from '@engine'
import { locomotiveSmokeFrag, locomotiveSmokeVert } from '@shaders'
import { useGameStore } from '@stores'

export interface SmokeOptions {
  offset?: Vector3Tuple
  scaleStart?: number
  scaleEnd?: number
  duration?: number
  rateMin?: number
  rateMax?: number
  radius?: number
  color?: Color
  velocityThreshold?: number
  maxParticles?: number
}

const SMOKE_TEXTURE = '/particles/smoke.png'

interface SmokeParticle {
  active: boolean
  position: Vector3
  velocity: Vector3
  life: number
  maxLife: number
  baseSize: number
  angle: number
}

interface SmokeSystem {
  points: Points<BufferGeometry, ShaderMaterial>
  update: (delta: number, pixelScale: number, absVelocity: number) => void
}

interface SmokeSystemParams {
  emitter: Object3D
  parent: Object3D
  texture: string
  offset?: Vector3
  scaleStart: number
  scaleEnd: number
  duration: number
  rateMin: number
  rateMax: number
  radius: number
  color: Color
  maxParticles: number
}

function createSmokeSystem({
  emitter,
  parent,
  texture,
  offset,
  scaleStart,
  scaleEnd,
  duration,
  rateMin,
  rateMax,
  radius,
  color,
  maxParticles,
}: SmokeSystemParams): SmokeSystem {
  const uniforms = {
    diffuseTexture: { value: new TextureLoader().load(texture) },
    pixelScale: { value: 1 },
  }

  const material = new ShaderMaterial({
    uniforms,
    vertexShader: locomotiveSmokeVert,
    fragmentShader: locomotiveSmokeFrag,
    blending: AdditiveBlending,
    depthTest: false,
    depthWrite: false,
    transparent: true,
    vertexColors: true,
  })

  const positionsArray = new Float32Array(maxParticles * 3)
  const sizesArray = new Float32Array(maxParticles)
  const colorsArray = new Float32Array(maxParticles * 4)
  const anglesArray = new Float32Array(maxParticles)

  const positionAttr = new BufferAttribute(positionsArray, 3).setUsage(DynamicDrawUsage)
  const sizeAttr = new BufferAttribute(sizesArray, 1).setUsage(DynamicDrawUsage)
  const colorAttr = new BufferAttribute(colorsArray, 4).setUsage(DynamicDrawUsage)
  const angleAttr = new BufferAttribute(anglesArray, 1).setUsage(DynamicDrawUsage)

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', positionAttr)
  geometry.setAttribute('size', sizeAttr)
  geometry.setAttribute('aColor', colorAttr)
  geometry.setAttribute('angle', angleAttr)

  const points = new Points(geometry, material)
  points.frustumCulled = false
  points.renderOrder = 10
  parent.add(points)

  const pool: SmokeParticle[] = Array.from({ length: maxParticles }, () => ({
    active: false,
    position: new Vector3(),
    velocity: new Vector3(),
    life: 0,
    maxLife: 0,
    baseSize: 0,
    angle: 0,
  }))

  let poolIndex = 0
  let accumulator = 0

  const emitOffset = offset ?? new Vector3()
  const worldPosition = new Vector3()
  const worldQuaternion = new Quaternion()
  const rotatedOffset = new Vector3()
  const spawnJitter = new Vector3()

  function spawn(delta: number, absVelocity: number) {
    const speedFactor = Math.min(absVelocity, 1)
    const rate = rateMin + (rateMax - rateMin) * speedFactor
    accumulator += delta * rate
    const count = Math.floor(accumulator)
    accumulator -= count

    if (count <= 0) return

    emitter.getWorldPosition(worldPosition)
    emitter.getWorldQuaternion(worldQuaternion)
    rotatedOffset.copy(emitOffset).applyQuaternion(worldQuaternion)

    for (let i = 0; i < count; i++) {
      for (let j = 0; j < maxParticles; j++) {
        const p = pool[poolIndex]
        poolIndex = (poolIndex + 1) % maxParticles

        if (!p.active) {
          p.active = true
          p.life = p.maxLife = duration + (Math.random() * 0.4 - 0.2)
          spawnJitter.set((Math.random() * 2 - 1) * radius, 0, (Math.random() * 2 - 1) * radius)
          p.position.copy(worldPosition).add(rotatedOffset).add(spawnJitter)
          p.velocity.set(0, 1.5, 0)
          p.baseSize = scaleEnd + (Math.random() * 0.4 - 0.2)
          p.angle = Math.random() * Math.PI * 2
          break
        }
      }
    }
  }

  function updateAndRender(delta: number) {
    let drawCount = 0

    for (let i = 0; i < maxParticles; i++) {
      const p = pool[i]
      if (!p.active) continue

      p.life -= delta
      if (p.life <= 0) {
        p.active = false
        continue
      }

      p.position.addScaledVector(p.velocity, delta)

      const t = 1 - p.life / p.maxLife
      const alpha = t < 0.2 ? t / 0.2 : Math.max(0, 1 - (t - 0.2) / 0.8)
      const size = scaleStart + (p.baseSize - scaleStart) * t

      positionsArray[drawCount * 3] = p.position.x
      positionsArray[drawCount * 3 + 1] = p.position.y
      positionsArray[drawCount * 3 + 2] = p.position.z
      sizesArray[drawCount] = size

      colorsArray[drawCount * 4] = color.r
      colorsArray[drawCount * 4 + 1] = color.g
      colorsArray[drawCount * 4 + 2] = color.b
      colorsArray[drawCount * 4 + 3] = alpha

      anglesArray[drawCount] = p.angle

      drawCount++
    }

    geometry.setDrawRange(0, drawCount)

    const hasParticles = drawCount > 0
    positionAttr.needsUpdate = hasParticles
    sizeAttr.needsUpdate = hasParticles
    colorAttr.needsUpdate = hasParticles
    angleAttr.needsUpdate = hasParticles
  }

  return {
    points,
    update: (delta: number, pixelScale: number, absVelocity: number) => {
      uniforms.pixelScale.value = pixelScale
      spawn(delta, absVelocity)
      updateAndRender(delta)
    },
  }
}

export interface LocomotiveSmokeProps extends SmokeOptions {
  vehicleId: string
  chimneyRef: RefObject<Object3D | null>
}

export function LocomotiveSmoke({ vehicleId, chimneyRef, ...options }: LocomotiveSmokeProps) {
  const {
    offset = [0, 0, 0],
    scaleStart = 0,
    scaleEnd = 3,
    duration = 1.5,
    rateMin = 1,
    rateMax = 20,
    radius = 0.02,
    color = new Color(0xffffff),
    velocityThreshold = 0.01,
    maxParticles = 100,
  } = options

  const { scene, size } = useThree()
  const particleSystemRef = useRef<SmokeSystem | null>(null)

  useEffect(() => {
    if (!chimneyRef.current) return

    const ps = createSmokeSystem({
      emitter: chimneyRef.current,
      parent: scene,
      offset: new Vector3().fromArray(offset),
      scaleStart,
      scaleEnd,
      duration,
      rateMin,
      rateMax,
      texture: SMOKE_TEXTURE,
      radius,
      color,
      maxParticles,
    })

    particleSystemRef.current = ps

    return () => {
      scene.remove(ps.points)
      ps.points.geometry.dispose()
      ;(ps.points.material as ShaderMaterial).uniforms.diffuseTexture?.value?.dispose()
      ;(ps.points.material as Material).dispose()
      particleSystemRef.current = null
    }
  }, [chimneyRef]) // eslint-disable-line react-hooks/exhaustive-deps

  useFrame(({ camera }, delta) => {
    if (!particleSystemRef.current) return

    const network = useGameStore.getState().network
    const vehicle = getVehicleById(network, vehicleId) as Locomotive
    if (!vehicle) return

    const velocity = vehicle.currentVelocity
    const absVelocity = Math.abs(velocity)
    const emissionVelocity = absVelocity < velocityThreshold ? 0 : absVelocity

    const pixelScale = camera.projectionMatrix.elements[0] * (size.width / 2)

    particleSystemRef.current.update(delta, pixelScale, emissionVelocity)
  })

  return null
}
